// @flow

import React, { Component } from 'react';
import { graphql } from '@apollo/client/react/hoc';
import { FormattedMessage } from 'react-intl';
import { withRouter } from 'react-router-dom';
import { event as ENUM_EVENT } from 'Enum';
import { UrlAssembler } from 'shared_services/riseart/utils/UrlAssembler';
import { application as CONFIG_APP } from 'Config';
import { LocationManager } from 'shared_services/riseart/url/Location';
import { MenuSwitch } from 'shared_components/common/menu/switch/Switch';
import { RsvpContext } from 'shared_data/providers/rsvp/Context';
import { getGraphqlOperationName, cacheUpdateHandler } from 'shared_services/apollo/helpers';
import LIST_ME_EVENT_RSVPS from 'shared_data/queries/event/listMeRsvps.graphql';

import CREATE_EVENT_RSVP_QUERY from 'shared_data/queries/event/createRsvp.graphql';

type OwnProps = {
  eventData: Object,
  forwardUrl?: string,
};

type Props = {
  ...OwnProps,
  animated: boolean,
  actionSetEventRsvpStatus: Function,
  isAllowed: boolean,
  createRsvp: Function,
  loaded: boolean,
  error: ?boolean,
  items: Array<Object>,
  loadingRsvpList: boolean,
  history: Object,
};

type State = {
  selectedStatus: ?number,
  isLoading: boolean,
};

const GQL_NAMES = {
  create: getGraphqlOperationName(CREATE_EVENT_RSVP_QUERY),
  listMeEventRsvps: getGraphqlOperationName(LIST_ME_EVENT_RSVPS),
};

/**
 * EventRsvpButtonComponent
 */
class EventRsvpButtonComponent extends Component<Props, State> {
  rsvpStatusList: Array<string>;

  static defaultProps = { animated: false };

  /**
   * constructor
   *
   * @param {Props} props
   */
  constructor(props: Props) {
    super(props);

    this.state = {
      selectedStatus: this.getRsvpStatus(),
      isLoading: !!this.props.loadingRsvpList,
    };
    this.rsvpStatusList = [
      ENUM_EVENT.rsvp.status.RSVP_YES,
      ENUM_EVENT.rsvp.status.RSVP_NO,
      ENUM_EVENT.rsvp.status.RSVP_MAYBE,
    ];

    this.bindMethods();
  }

  /**
   * componentDidUpdate
   *
   * @param {Props} prevProps
   */
  componentDidUpdate(prevProps: Props) {
    let updatedState = null;

    // Update loading state if props are different
    if (this.props.loadingRsvpList !== prevProps.loadingRsvpList) {
      updatedState = {
        ...(updatedState || {}),
        isLoading: this.props.loadingRsvpList,
      };
    }

    // Update component state if prop eventId id different or if rsvp list loaded
    if (
      this.props.eventData.id !== prevProps.eventData.id ||
      this.props.loaded !== prevProps.loaded ||
      this.getRsvpStatus() !== this.getRsvpStatus(prevProps)
    ) {
      updatedState = {
        ...(updatedState || {}),
        selectedStatus: this.getRsvpStatus(),
      };
    }

    if (updatedState) {
      this.setState(updatedState);
    }
  }

  /**
   * handleRsvp
   *
   * @param {number} value
   */
  handleRsvp: Function;

  handleRsvp(value: number) {
    if (this.state.isLoading) {
      return;
    }

    // Optimistic update of selected RSVP status
    this.setState({ isLoading: true, selectedStatus: value });

    // Call create RSVP mutation
    this.props
      .createRsvp(value)
      .then(() => {
        this.props.actionSetEventRsvpStatus({
          event: { ...this.props.eventData },
          selectedStatus: value,
        });
        this.setState({ isLoading: false });
      })
      .catch(() => {
        this.setState({ isLoading: false, selectedStatus: null });
      });
  }

  /**
   * handleAuthFormRedirect
   *
   * @param {number} value
   */
  handleAuthFormRedirect: Function;

  handleAuthFormRedirect(value: number) {
    this.props.history.push(
      UrlAssembler.byRouteKey('login', {
        search: {
          role: CONFIG_APP.acl.resourcePermissions.event.rsvp,
          forward: this.props.forwardUrl || LocationManager.get('uri'),
        },
        hash: { event: this.props.eventData.id, rsvp: `${value}` },
      }),
    );
  }

  /**
   * getRsvpStatus
   *
   * @returns {?Object} found item or undefined
   */
  getRsvpStatus(data?: Object): ?Object {
    const { eventData, items } = data || this.props;

    return items[eventData.id];
  }

  /**
   * bindMethods
   */
  bindMethods() {
    this.handleRsvp = this.handleRsvp.bind(this);
    this.handleAuthFormRedirect = this.handleAuthFormRedirect.bind(this);
  }

  /**
   * render
   */
  render() {
    const { selectedStatus } = this.state;
    const { isAllowed, animated, loaded, eventData } = this.props;

    return eventData && eventData.canRsvp && loaded ? (
      <MenuSwitch
        animated={animated}
        label={
          <React.Fragment>
            <FormattedMessage id="components.event.rsvpBtnLabel.line1">
              {(label: string) => label}
            </FormattedMessage>
            <br />
            <FormattedMessage id="components.event.rsvpBtnLabel.line2">
              {(label: string) => label}
            </FormattedMessage>
          </React.Fragment>
        }
        options={this.rsvpStatusList.map((value) => ({
          value,
          label: (
            <FormattedMessage id={`components.event.rsvpStatus.${value}`}>
              {(item: string) => item}
            </FormattedMessage>
          ),
        }))}
        selected={parseInt(selectedStatus, 10)}
        onClick={isAllowed ? this.handleRsvp : this.handleAuthFormRedirect}
      />
    ) : null;
  }
}

/**
 * withCreateRsvp
 */
// $FlowFixMe
export const withCreateRsvp = graphql(CREATE_EVENT_RSVP_QUERY, {
  props: ({ ownProps, mutate }: Object) => ({
    createRsvp(status, id) {
      return mutate({
        variables: {
          eventId: parseInt(id || ownProps.eventData.id, 10),
          userId: parseInt(ownProps.userId, 10),
          status: parseInt(status, 10),
        },
        update: cacheUpdateHandler(
          LIST_ME_EVENT_RSVPS,
          GQL_NAMES.create,
          null,
          (cache, updatedData) => {
            const currentData = cache.readQuery({ query: LIST_ME_EVENT_RSVPS });

            if (
              !updatedData ||
              !currentData ||
              !currentData.listMeEventRsvps ||
              !currentData.listMeEventRsvps.items
            ) {
              return;
            }

            let newItems = [];

            if (currentData.listMeEventRsvps.items.some((item) => item.id === updatedData.id)) {
              newItems = currentData.listMeEventRsvps.items.map((item) =>
                item.id === updatedData.id ? updatedData : item,
              );
            } else {
              newItems = [...currentData.listMeEventRsvps.items, updatedData];
            }

            // Add new RSVP record to query cache
            cache.writeQuery({
              query: LIST_ME_EVENT_RSVPS,
              data: {
                [GQL_NAMES.listMeEventRsvps]: {
                  ...currentData.listMeEventRsvps,
                  items: newItems,
                },
              },
            });
          },
        ),
      });
    },
  }),
});

const EventRsvpButtonWithMutations = withRouter(withCreateRsvp(EventRsvpButtonComponent));

/**
 * EventRsvpButton
 *
 * @param {OwnProps} ownProps
 */
export const EventRsvpButton = (ownProps: OwnProps) => (
  <RsvpContext.Consumer>
    {(rsvpProps) => <EventRsvpButtonWithMutations {...ownProps} {...rsvpProps} />}
  </RsvpContext.Consumer>
);
